import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { login } from '../services/authService';
import { getLoginTitle, getPostLoginPath } from '../utils/panel';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login: setAuthState } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Sayfa yüklendiğinde input'ları temizle
  useEffect(() => {
    setUsername('');
    setPassword('');
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Client-side validasyon
      if (!username.trim()) {
        setError('Kullanıcı adı gereklidir');
        setIsLoading(false);
        return;
      }

      if (username.trim().length < 3) {
        setError('Kullanıcı adı en az 3 karakter olmalıdır');
        setIsLoading(false);
        return;
      }

      if (username.trim().length > 50) {
        setError('Kullanıcı adı en fazla 50 karakter olabilir');
        setIsLoading(false);
        return;
      }

      if (!password.trim()) {
        setError('Şifre gereklidir');
        setIsLoading(false);
        return;
      }

      if (password.length < 6) {
        setError('Şifre en az 6 karakter olmalıdır');
        setIsLoading(false);
        return;
      }

      // Login işlemi
      console.log('[Login] Attempting login with:', { username: username.trim(), passwordLength: password.length });
      const result = await login(username, password);

      console.log('[Login] Login result:', result);

      if (result.success || result.basarili) {
        // Token'lar authService'de kaydedildi, şimdi user bilgisini AuthContext'e kaydet
        const userData = result.user || result.veri?.kullanici || { kullaniciAdi: username.trim() };
        if (result.veri?.kullanici && !userData.rol) userData.rol = result.veri.kullanici.rol;
        console.log('[Login] Setting auth state with user:', userData);
        
        // AuthContext'in login fonksiyonunu çağır (state'i günceller)
        await setAuthState(userData);

        navigate(getPostLoginPath(userData.rol), { replace: true });
      } else {
        const errorMsg = result.mesaj || result.message || 'Giriş başarısız';
        console.error('[Login] Login failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      // Hata mesajını güvenli şekilde göster
      console.error('[Login] Login error:', err);
      const errorMessage = err?.message || err?.mesaj || 'Giriş yapılırken bir hata oluştu';
      console.error('[Login] Error details:', {
        message: errorMessage,
        status: err?.status,
        data: err?.data,
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    // Kullanıcı adı input'u için sanitize etme (sanitizeUserName login sırasında yapılacak)
    // Sadece normal karakterleri kabul et
    setUsername(value);
    if (error) setError('');
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (error) setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/assets/img/logo.png" alt="Firma Logo" />
          </div>
          <h1>{getLoginTitle()}</h1>
          <p>Hesabınıza giriş yapın</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Kullanıcı Adı</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={handleUsernameChange}
              placeholder="Kullanıcı Adı"
              required
              autoComplete="username"
              disabled={isLoading}
              minLength={3}
              maxLength={50}
              className={error && !username ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Şifre</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
                autoComplete="current-password"
                disabled={isLoading}
                minLength={6}
                className={error && !password ? 'input-error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>

        <div className="login-footer">
          <p className="security-note">
            🔒 Güvenli bağlantı ile korunmaktadır
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
